import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CreditCard, Lock, AlertTriangle } from "lucide-react";
import { load } from "@cashfreepayments/cashfree-js";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const FEE = Number(process.env.REACT_APP_SIGNUP_FEE_INR || 69);
const CASHFREE_ENV = process.env.REACT_APP_CASHFREE_ENV || "sandbox";

const COPY = {
  payment_pending: {
    icon: Lock, chip: "bg-amber-100 text-amber-800",
    title: "Activate your account to attempt mock tests",
    body: (`Pay a one-time ₹${FEE} activation fee. Your dashboard stays read-only until payment is confirmed.`),
  },
  suspended: {
    icon: AlertTriangle, chip: "bg-red-100 text-red-800",
    title: "Your account was deactivated by the admin",
    body: (`You can view your dashboard and past results but can't attempt new tests. Pay ₹${FEE} to reactivate your account instantly, or contact your administrator.`),
  },
};

export const SignupPaymentBanner = ({ reason = "payment_pending" }) => {
  const [busy, setBusy] = useState(false);
  const cfg = COPY[reason] || COPY.payment_pending;
  const Icon = cfg.icon;

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/signup/create");
      const cashfree = await load({ mode: data.env === "production" ? "production" : "sandbox" });
      if (!cashfree) throw new Error("Could not load payment gateway");
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className={`mt-4 border rounded-xl px-5 py-4 ${reason === "suspended" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`} data-testid="dashboard-payment-banner">
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${reason === "suspended" ? "text-red-700" : "text-amber-700"}`} />
        <div className="flex-1 min-w-0">
          <div className={`font-semibold ${reason === "suspended" ? "text-red-900" : "text-amber-900"}`}>{cfg.title}</div>
          <p className={`text-sm mt-0.5 ${reason === "suspended" ? "text-red-800" : "text-amber-800"}`}>
            {cfg.body}
            {CASHFREE_ENV !== "production" && <span className="ml-1 italic opacity-80">(Sandbox mode — test cards only.)</span>}
          </p>
        </div>
        <Button onClick={pay} disabled={busy} data-testid="dashboard-pay-button"
          className={`shrink-0 text-white ${reason === "suspended" ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
          {reason === "suspended" ? `Reactivate · ₹${FEE}` : `Pay ₹${FEE}`}
        </Button>
      </div>
    </div>
  );
};
